const CHUNK_RELOAD_STATE_KEY = 'webadmin:chunk-reload-state';
const CHUNK_RELOAD_QUERY_PARAM = '__app_reload';
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

type ChunkFailureSource =
  | 'vite:preloadError'
  | 'window:error'
  | 'window:unhandledrejection'
  | 'lazy:import';

interface ChunkReloadState {
  at: number;
  fromHref: string;
  signature: string;
  source: ChunkFailureSource;
}

let chunkRecoveryInitialized = false;

const CHUNK_ERROR_MARKERS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'unable to preload css for',
  'loading css chunk',
  'loading chunk',
  'chunkloaderror',
  'outdated optimize dep',
  'optimize deps directory',
  'dep optimizer',
  'the file does not exist at',
];

const RESOURCE_URL_PATTERN =
  /(https?:\/\/[^\s"'`)]+(?:\/webadmin\/app\/assets\/|\/node_modules\/\.vite\/deps\/)[^\s"'`)]+|(?:\/webadmin\/app\/assets\/|\/node_modules\/\.vite\/deps\/)[^\s"'`)]+)/i;

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || '';
  }
  if (error instanceof ErrorEvent) {
    return error.message || '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    const maybeMessage = (error as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') {
      return maybeMessage;
    }
  }
  return '';
}

function extractAssetUrlFromText(text: string): string | null {
  const match = text.match(RESOURCE_URL_PATTERN);
  return match?.[1] ?? null;
}

function isRecoverableResourceUrl(url: string): boolean {
  try {
    const fallbackOrigin = 'https://erp24.uk';
    const origin = typeof window === 'undefined' ? fallbackOrigin : window.location.origin;
    const parsed = new URL(url, origin);
    return (
      parsed.pathname.startsWith('/webadmin/app/assets/') ||
      parsed.pathname.startsWith('/node_modules/.vite/deps/')
    );
  } catch {
    return false;
  }
}

function isLikelyChunkLoadFailure(message: string, assetUrl: string | null): boolean {
  const normalizedMessage = message.trim().toLowerCase();
  if (CHUNK_ERROR_MARKERS.some((marker) => normalizedMessage.includes(marker))) {
    return true;
  }
  if (!assetUrl || !isRecoverableResourceUrl(assetUrl)) {
    return false;
  }
  if (!normalizedMessage) {
    return true;
  }
  return (
    normalizedMessage.includes('failed') ||
    normalizedMessage.includes('504') ||
    normalizedMessage.includes('404') ||
    normalizedMessage.includes('optimize') ||
    normalizedMessage.includes('preload') ||
    normalizedMessage.includes('network')
  );
}

function readReloadState(): ChunkReloadState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RELOAD_STATE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ChunkReloadState>;
    if (
      typeof parsed.at !== 'number' ||
      typeof parsed.fromHref !== 'string' ||
      typeof parsed.signature !== 'string' ||
      typeof parsed.source !== 'string'
    ) {
      return null;
    }
    return {
      at: parsed.at,
      fromHref: parsed.fromHref,
      signature: parsed.signature,
      source: parsed.source as ChunkFailureSource,
    };
  } catch {
    return null;
  }
}

function writeReloadState(state: ChunkReloadState): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_STATE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage failures (private mode/quota exceeded).
  }
}

function clearReloadState(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_STATE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function shouldAutoReload(
  now: number,
  previous: Pick<ChunkReloadState, 'at'> | null,
  online: boolean,
  cooldownMs = CHUNK_RELOAD_COOLDOWN_MS,
): boolean {
  if (!online) {
    return false;
  }
  if (!previous) {
    return true;
  }
  return now - previous.at >= cooldownMs;
}

function buildReloadHref(currentHref: string, now: number): string {
  const nextUrl = new URL(currentHref);
  nextUrl.searchParams.set(CHUNK_RELOAD_QUERY_PARAM, String(now));
  return nextUrl.toString();
}

function clearReloadQueryParam(): void {
  const currentUrl = new URL(window.location.href);
  if (!currentUrl.searchParams.has(CHUNK_RELOAD_QUERY_PARAM)) {
    return;
  }
  currentUrl.searchParams.delete(CHUNK_RELOAD_QUERY_PARAM);
  window.history.replaceState(window.history.state, document.title, currentUrl.toString());
}

function pruneExpiredReloadState(now: number): void {
  const previous = readReloadState();
  if (!previous) {
    return;
  }
  if (now - previous.at >= CHUNK_RELOAD_COOLDOWN_MS) {
    clearReloadState();
  }
}

function extractAssetUrlFromEvent(event: Event): string | null {
  const target = event.target;
  if (target instanceof HTMLScriptElement) {
    return target.src || null;
  }
  if (target instanceof HTMLLinkElement) {
    return target.href || null;
  }
  return null;
}

function attemptChunkRecovery(
  source: ChunkFailureSource,
  error: unknown,
  explicitAssetUrl: string | null = null,
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const message = extractErrorMessage(error);
  const assetUrl = explicitAssetUrl || extractAssetUrlFromText(message);

  if (!isLikelyChunkLoadFailure(message, assetUrl)) {
    return false;
  }

  const now = Date.now();
  const previous = readReloadState();
  if (!shouldAutoReload(now, previous, window.navigator.onLine)) {
    return false;
  }

  const signature = assetUrl || message || source;
  writeReloadState({
    at: now,
    fromHref: window.location.href,
    signature,
    source,
  });

  window.location.replace(buildReloadHref(window.location.href, now));
  return true;
}

function onVitePreloadError(event: VitePreloadErrorEvent): void {
  if (attemptChunkRecovery('vite:preloadError', event.payload)) {
    event.preventDefault();
  }
}

function onWindowUnhandledRejection(event: PromiseRejectionEvent): void {
  if (attemptChunkRecovery('window:unhandledrejection', event.reason)) {
    event.preventDefault();
  }
}

function onWindowError(event: Event): void {
  const resourceUrl = extractAssetUrlFromEvent(event);
  const payload =
    event instanceof ErrorEvent ? event.error ?? event.message : resourceUrl ? new Error(resourceUrl) : event;

  if (attemptChunkRecovery('window:error', payload, resourceUrl)) {
    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
  }
}

export function initChunkLoadAutoReload(): void {
  if (chunkRecoveryInitialized || typeof window === 'undefined') {
    return;
  }
  chunkRecoveryInitialized = true;

  clearReloadQueryParam();
  pruneExpiredReloadState(Date.now());

  window.addEventListener('vite:preloadError', onVitePreloadError);
  window.addEventListener('unhandledrejection', onWindowUnhandledRejection);
  window.addEventListener('error', onWindowError, true);
}

export function recoverFromChunkLoadError(
  error: unknown,
  source: ChunkFailureSource = 'lazy:import',
  explicitAssetUrl: string | null = null,
): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return attemptChunkRecovery(source, error, explicitAssetUrl);
}

export function __extractAssetUrlFromTextForTests(text: string): string | null {
  return extractAssetUrlFromText(text);
}

export function __isLikelyChunkLoadFailureForTests(message: string, assetUrl: string | null = null): boolean {
  return isLikelyChunkLoadFailure(message, assetUrl);
}

export function __buildReloadHrefForTests(currentHref: string, now: number): string {
  return buildReloadHref(currentHref, now);
}

export function __shouldAutoReloadForTests(
  now: number,
  previous: Pick<ChunkReloadState, 'at'> | null,
  online: boolean,
  cooldownMs = CHUNK_RELOAD_COOLDOWN_MS,
): boolean {
  return shouldAutoReload(now, previous, online, cooldownMs);
}
